import { useEffect, useState } from "react";
import { CalendarDays, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Timetable } from "@/components/Timetable";
import EmployeeProfileDialog from "@/components/profile/EmployeeProfileDialog";

const inputCls = "h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";

/** Weekly timetable per teacher; editing happens in the teacher profile → Schedule tab. */
export function TeacherSchedulesDialog({ open, onOpenChange }) {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teacherId, setTeacherId] = useState("");
  const [editId, setEditId] = useState(null);

  const load = async (keep) => {
    try {
      const [o, c] = await Promise.all([api.get("/officers"), api.get("/classes")]);
      const ts = o.data.filter((p) => p.role === "teacher");
      setTeachers(ts); setClasses(c.data);
      setTeacherId((cur) => (keep && cur) || ts[0]?.id || "");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"); }
  };
  useEffect(() => { if (open) load(false); }, [open]);

  const teacher = teachers.find((t) => t.id === teacherId);
  return (
    <>
      <Dialog open={open && !editId} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="teacher-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><CalendarDays className="h-5 w-5 text-primary" /> Teacher schedules</DialogTitle>
            <DialogDescription>Weekly timetable built from each teacher's periods, rooms and days. Click a period or "Edit schedule" to change it.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <select value={teacherId} onChange={(e) => setTeacherId(e.target.value)} data-testid="teacher-schedule-select" className={`${inputCls} max-w-xs flex-1`}>
              {teachers.length === 0 && <option value="">No teachers yet</option>}
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button type="button" disabled={!teacher} onClick={() => setEditId(teacherId)} data-testid="teacher-schedule-edit-btn"
              className="h-10 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:shadow disabled:opacity-40 transition">
              <Pencil className="h-4 w-4" /> Edit schedule
            </button>
          </div>
          {teachers.length === 0
            ? <div className="rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="teacher-schedule-empty">No teachers yet — add one on the Employees page with the "Teacher" role.</div>
            : <Timetable classes={classes.filter((c) => c.teacher_id === teacherId)} onSelect={() => setEditId(teacherId)} testid="teacher-timetable" />}
        </DialogContent>
      </Dialog>
      {editId && <EmployeeProfileDialog userId={editId} companyType="school" initialTab="schedule" onClose={() => { setEditId(null); load(true); }} />}
    </>
  );
}
